import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#FFB300";
const CREAM = "#EDEBE4";
const BG = "#050505";
const BG_ALT = "#0A0A0A";
const BG_ELEV = "#101010";

const SESSION_KEY = "debtlabs_network_session";
const FONT_IMPORT =
  "@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;1,9..144,400&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');";

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter((p) => p && !p.includes(","))
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const monoStyle = {
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
};

// =============================================================
// Avatar
// =============================================================
function MemberAvatar({ member, size = 40 }) {
  if (member?.photo_url) {
    return (
      <img
        src={member.photo_url}
        alt={member.name}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          border: `1px solid ${GOLD}33`,
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${GOLD}, #a8875a)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: BG,
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 600,
        fontSize: size * 0.35,
        flexShrink: 0,
      }}
    >
      {initials(member?.name)}
    </div>
  );
}

// =============================================================
// Auth landing — claim invite
// =============================================================
export function NetworkAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | error
  const [err, setErr] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get("token");
    if (!inviteToken) {
      setStatus("error");
      setErr("Missing invite token.");
      return;
    }
    axios
      .post(`${API}/network/auth`, { invite_token: inviteToken })
      .then(({ data }) => {
        localStorage.setItem(SESSION_KEY, data.session_token);
        navigate("/network", { replace: true });
      })
      .catch((e) => {
        setStatus("error");
        setErr(e.response?.data?.detail || "Invalid or expired invite.");
      });
  }, [location.search, navigate]);

  return (
    <div
      data-testid="network-auth-page"
      style={{
        background: BG,
        color: CREAM,
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <style>{`${FONT_IMPORT} body { background: ${BG}; margin: 0; }`}</style>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <LogoMark mode="dark" size={48} />
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 32,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            marginTop: 26,
            marginBottom: 12,
          }}
        >
          {status === "checking" ? "Activating your access…" : "Invite issue"}
        </h1>
        {status === "checking" && (
          <div
            style={{
              ...monoStyle,
              fontSize: 10,
              color: GOLD,
              marginTop: 18,
              opacity: 0.85,
            }}
          >
            verifying invite token
          </div>
        )}
        {status === "error" && (
          <>
            <p style={{ fontSize: 14, opacity: 0.75, lineHeight: 1.6, marginBottom: 24 }}>{err}</p>
            <Link
              to="/network"
              style={{
                ...monoStyle,
                fontSize: 10,
                background: GOLD,
                color: BG,
                padding: "10px 18px",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              go to network
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================
// Login wall — shown when no session
// =============================================================
function LoginWall() {
  return (
    <div
      data-testid="network-login-wall"
      style={{
        background: BG,
        color: CREAM,
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <style>{`${FONT_IMPORT} body { background: ${BG}; margin: 0; }`}</style>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <LogoMark mode="dark" size={48} />
        <div
          style={{
            ...monoStyle,
            fontSize: 10,
            color: GOLD,
            marginTop: 22,
            marginBottom: 18,
            opacity: 0.85,
          }}
        >
          — THE DEBT GUY NETWORK
        </div>
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 38,
            fontWeight: 400,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            marginBottom: 18,
          }}
        >
          The room for{" "}
          <span style={{ color: GOLD, fontStyle: "italic" }}>
            cohort members and sherpas
          </span>
          .
        </h1>
        <p
          style={{
            fontSize: 14.5,
            opacity: 0.72,
            lineHeight: 1.65,
            maxWidth: 380,
            margin: "0 auto 36px",
            fontWeight: 300,
          }}
        >
          Private members area. Directory of operators, war stories, deal-flow board, real
          conversations off the public internet. Invite-only.
        </p>
        <Link
          to="/"
          data-testid="network-back-home"
          style={{
            ...monoStyle,
            fontSize: 10,
            color: CREAM,
            opacity: 0.65,
            textDecoration: "none",
            border: `1px solid ${CREAM}22`,
            padding: "10px 18px",
            display: "inline-block",
          }}
        >
          ← back to landing
        </Link>
      </div>
    </div>
  );
}

// =============================================================
// Tabs
// =============================================================
const TABS = [
  { id: "feed", label: "feed" },
  { id: "directory", label: "directory" },
];

// =============================================================
// Main app — directory + feed + post composer
// =============================================================
function NetworkApp({ session, onLogout }) {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("feed");
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${session}` } };

  const loadAll = useCallback(async () => {
    try {
      const [meRes, membersRes, postsRes] = await Promise.all([
        axios.get(`${API}/network/me`, auth),
        axios.get(`${API}/network/members`, auth),
        axios.get(`${API}/network/posts`, auth),
      ]);
      setMe(meRes.data.member);
      setMembers(membersRes.data.members);
      setPosts(postsRes.data.posts);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <div
      data-testid="network-app"
      style={{
        background: BG,
        color: CREAM,
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`${FONT_IMPORT} body { background: ${BG}; margin: 0; } * { box-sizing: border-box; }`}</style>

      {/* NAV */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 28px",
          background: "rgba(14,13,11,0.95)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${CREAM}0e`,
        }}
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit" }}>
          <LogoMark mode="dark" size={32} />
          <div>
            <div
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 16,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              The Debt Guy · Network
            </div>
            <div
              style={{
                ...monoStyle,
                fontSize: 8.5,
                opacity: 0.55,
                marginTop: 3,
              }}
            >
              MEMBERS ONLY
            </div>
          </div>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {me && (
            <div
              data-testid="network-current-member"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <MemberAvatar member={me} size={32} />
              <div style={{ fontSize: 12, opacity: 0.85 }}>{me.name}</div>
            </div>
          )}
          <button
            onClick={onLogout}
            data-testid="network-logout"
            style={{
              ...monoStyle,
              fontSize: 9,
              background: "transparent",
              border: `1px solid ${CREAM}22`,
              color: CREAM,
              padding: "7px 12px",
              cursor: "pointer",
              opacity: 0.65,
            }}
          >
            sign out
          </button>
        </div>
      </nav>

      {/* TAB STRIP */}
      <div
        style={{
          display: "flex",
          gap: 24,
          padding: "20px 28px 0",
          maxWidth: 1100,
          margin: "0 auto",
          width: "100%",
          borderBottom: `1px solid ${CREAM}0e`,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`network-tab-${t.id}`}
            onClick={() => {
              setTab(t.id);
              setOpenPost(null);
            }}
            style={{
              ...monoStyle,
              fontSize: 10,
              background: "transparent",
              border: "none",
              color: tab === t.id ? GOLD : CREAM,
              padding: "12px 0",
              borderBottom: `2px solid ${tab === t.id ? GOLD : "transparent"}`,
              cursor: "pointer",
              opacity: tab === t.id ? 1 : 0.55,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 28px 80px" }}>
        {tab === "feed" && !openPost && (
          <FeedView
            posts={posts}
            onCompose={() => setComposerOpen(true)}
            onOpenPost={(p) => setOpenPost(p)}
          />
        )}
        {tab === "feed" && openPost && (
          <PostDetail
            session={session}
            postId={openPost.id}
            onBack={() => {
              setOpenPost(null);
              loadAll();
            }}
            onLogout={onLogout}
          />
        )}
        {tab === "directory" && <DirectoryView members={members} />}
      </main>

      {composerOpen && (
        <Composer
          session={session}
          onClose={() => setComposerOpen(false)}
          onCreated={() => {
            setComposerOpen(false);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

// =============================================================
// Feed view
// =============================================================
function FeedView({ posts, onCompose, onOpenPost }) {
  return (
    <div data-testid="network-feed-view">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 28,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 30,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          The room.
        </h2>
        <div style={{ flex: 1 }} />
        <button
          onClick={onCompose}
          data-testid="network-compose-btn"
          style={{
            ...monoStyle,
            fontSize: 10,
            background: GOLD,
            color: BG,
            border: "none",
            padding: "10px 18px",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          + ask the room
        </button>
      </div>

      {posts.length === 0 ? (
        <div
          data-testid="network-empty-feed"
          style={{
            border: `1px dashed ${GOLD}55`,
            padding: "48px 28px",
            textAlign: "center",
            background: BG_ALT,
          }}
        >
          <div style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.85, marginBottom: 14 }}>
            — the room is quiet
          </div>
          <p style={{ fontSize: 15, opacity: 0.75, lineHeight: 1.6, maxWidth: 480, margin: "0 auto 22px" }}>
            Be the first to ask a question, share a war story, or post a deal-flow opportunity.
          </p>
          <button
            onClick={onCompose}
            style={{
              ...monoStyle,
              fontSize: 10,
              background: GOLD,
              color: BG,
              border: "none",
              padding: "10px 18px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            + start a thread
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 1, background: `${CREAM}11` }}>
          {posts.map((p) => (
            <button
              key={p.id}
              onClick={() => onOpenPost(p)}
              data-testid={`network-post-${p.id}`}
              style={{
                background: BG_ALT,
                padding: "20px 24px",
                cursor: "pointer",
                border: "none",
                color: CREAM,
                textAlign: "left",
                display: "flex",
                alignItems: "flex-start",
                gap: 16,
                transition: "background 0.2s",
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = BG_ELEV)}
              onMouseLeave={(e) => (e.currentTarget.style.background = BG_ALT)}
            >
              <MemberAvatar member={p.author} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                  <span
                    style={{
                      ...monoStyle,
                      fontSize: 8,
                      padding: "2px 6px",
                      border: `1px solid ${GOLD}55`,
                      color: GOLD,
                    }}
                  >
                    {(p.category || "general").replace("-", " ")}
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.55 }}>{p.author?.name}</span>
                  <span style={{ fontSize: 11, opacity: 0.4 }}>· {formatRelative(p.last_activity)}</span>
                </div>
                <h3
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 20,
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.25,
                    margin: "4px 0",
                  }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    opacity: 0.65,
                    margin: 0,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {p.body}
                </p>
              </div>
              <div style={{ textAlign: "right", ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.85, minWidth: 70 }}>
                {p.reply_count} {p.reply_count === 1 ? "reply" : "replies"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
// Composer modal
// =============================================================
function Composer({ session, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("ask-the-room");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      setErr("Title and body are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/network/posts`,
        { title, body, category },
        { headers: { Authorization: `Bearer ${session}` } }
      );
      onCreated();
    } catch {
      setErr("Failed to post. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${CREAM}1f`,
    color: CREAM,
    fontFamily: "'Inter', sans-serif",
    fontSize: 15,
    padding: "10px 0",
    outline: "none",
    borderRadius: 0,
  };

  return (
    <div
      data-testid="network-composer"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,6,5,0.9)",
        backdropFilter: "blur(10px)",
        zIndex: 100,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BG_ALT,
          border: `1px solid ${GOLD}44`,
          maxWidth: 640,
          width: "100%",
          padding: "36px 40px 28px",
        }}
      >
        <div
          style={{
            ...monoStyle,
            fontSize: 10,
            color: GOLD,
            marginBottom: 8,
          }}
        >
          — NEW POST
        </div>
        <h2
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 26,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            marginBottom: 22,
          }}
        >
          Ask the room.
        </h2>

        <div style={{ marginBottom: 18 }}>
          <label
            style={{
              ...monoStyle,
              fontSize: 9,
              color: GOLD,
              opacity: 0.85,
              display: "block",
              marginBottom: 6,
            }}
          >
            category
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { v: "ask-the-room", l: "ask the room" },
              { v: "deal-flow", l: "deal flow" },
              { v: "general", l: "general" },
            ].map((c) => (
              <button
                key={c.v}
                onClick={() => setCategory(c.v)}
                data-testid={`composer-cat-${c.v}`}
                style={{
                  ...monoStyle,
                  fontSize: 9,
                  background: category === c.v ? `${GOLD}22` : "transparent",
                  border: `1px solid ${category === c.v ? GOLD : CREAM + "22"}`,
                  color: category === c.v ? GOLD : CREAM,
                  padding: "6px 10px",
                  cursor: "pointer",
                }}
              >
                {c.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label
            style={{
              ...monoStyle,
              fontSize: 9,
              color: GOLD,
              opacity: 0.85,
              display: "block",
              marginBottom: 4,
            }}
          >
            title
          </label>
          <input
            data-testid="composer-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            maxLength={160}
            placeholder="Anyone closed a Mintos warehouse?"
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label
            style={{
              ...monoStyle,
              fontSize: 9,
              color: GOLD,
              opacity: 0.85,
              display: "block",
              marginBottom: 4,
            }}
          >
            body
          </label>
          <textarea
            data-testid="composer-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={4000}
            style={{
              ...inputStyle,
              borderBottom: `1px solid ${CREAM}1f`,
              resize: "vertical",
              minHeight: 120,
              fontFamily: "'Inter', sans-serif",
            }}
            placeholder="Looking for war stories on advance rates and concentration limits…"
          />
        </div>

        {err && <p style={{ color: "#ff8080", fontSize: 12, marginBottom: 14 }}>{err}</p>}

        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <button
            onClick={onClose}
            style={{
              ...monoStyle,
              fontSize: 10,
              background: "transparent",
              border: `1px solid ${CREAM}2a`,
              color: CREAM,
              padding: "12px 20px",
              cursor: "pointer",
            }}
          >
            cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="composer-submit"
            style={{
              ...monoStyle,
              fontSize: 10,
              background: GOLD,
              color: BG,
              border: "none",
              padding: "12px 26px",
              cursor: submitting ? "wait" : "pointer",
              fontWeight: 500,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "posting…" : "post →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Post detail
// =============================================================
function PostDetail({ session, postId, onBack, onLogout }) {
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${session}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/network/posts/${postId}`, auth);
      setPost(data.post);
      setReplies(data.replies);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, session]);

  useEffect(() => {
    load();
  }, [load]);

  const submitReply = async () => {
    if (!reply.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/network/posts/${postId}/replies`, { body: reply }, auth);
      setReply("");
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) return <div style={{ opacity: 0.5 }}>loading…</div>;

  return (
    <div data-testid={`network-post-detail-${postId}`}>
      <button
        onClick={onBack}
        data-testid="network-post-back"
        style={{
          ...monoStyle,
          fontSize: 9,
          background: "transparent",
          border: `1px solid ${CREAM}22`,
          color: CREAM,
          padding: "6px 12px",
          cursor: "pointer",
          opacity: 0.7,
          marginBottom: 22,
        }}
      >
        ← back to feed
      </button>

      <div style={{ marginBottom: 14 }}>
        <span
          style={{
            ...monoStyle,
            fontSize: 8,
            padding: "2px 6px",
            border: `1px solid ${GOLD}55`,
            color: GOLD,
          }}
        >
          {(post.category || "general").replace("-", " ")}
        </span>
      </div>

      <h1
        style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 36,
          fontWeight: 400,
          letterSpacing: "-0.025em",
          lineHeight: 1.15,
          marginBottom: 16,
        }}
      >
        {post.title}
      </h1>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <MemberAvatar member={post.author} size={36} />
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>{post.author?.name}</div>
          <div style={{ ...monoStyle, fontSize: 9, opacity: 0.55, marginTop: 2 }}>
            {post.author?.role || "member"} · {formatRelative(post.created_at)}
          </div>
        </div>
      </div>

      <div
        style={{
          fontSize: 16,
          lineHeight: 1.7,
          opacity: 0.88,
          whiteSpace: "pre-wrap",
          marginBottom: 40,
          maxWidth: 720,
        }}
      >
        {post.body}
      </div>

      <div style={{ borderTop: `1px solid ${CREAM}14`, paddingTop: 28, marginBottom: 24 }}>
        <div
          style={{
            ...monoStyle,
            fontSize: 10,
            color: GOLD,
            opacity: 0.85,
            marginBottom: 18,
          }}
        >
          {replies.length} {replies.length === 1 ? "reply" : "replies"}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {replies.map((r) => (
            <div
              key={r.id}
              data-testid={`network-reply-${r.id}`}
              style={{
                background: BG_ALT,
                border: `1px solid ${CREAM}0e`,
                padding: "16px 20px",
                display: "flex",
                gap: 14,
              }}
            >
              <MemberAvatar member={r.author} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{r.author?.name}</span>
                  <span style={{ ...monoStyle, fontSize: 9, opacity: 0.5 }}>
                    {formatRelative(r.created_at)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    opacity: 0.85,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {r.body}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 28 }}>
        <textarea
          data-testid="network-reply-input"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Reply with your war story, advice, or question…"
          maxLength={4000}
          style={{
            width: "100%",
            background: BG_ALT,
            border: `1px solid ${CREAM}1f`,
            color: CREAM,
            padding: "12px 14px",
            fontFamily: "'Inter', sans-serif",
            fontSize: 14,
            resize: "vertical",
            minHeight: 90,
            outline: "none",
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            onClick={submitReply}
            disabled={submitting || !reply.trim()}
            data-testid="network-reply-submit"
            style={{
              ...monoStyle,
              fontSize: 10,
              background: reply.trim() && !submitting ? GOLD : `${GOLD}44`,
              color: BG,
              border: "none",
              padding: "10px 20px",
              cursor: reply.trim() && !submitting ? "pointer" : "not-allowed",
              fontWeight: 500,
            }}
          >
            {submitting ? "posting…" : "post reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Directory
// =============================================================
function DirectoryView({ members }) {
  const [q, setQ] = useState("");
  const filtered = members.filter((m) => {
    if (!q) return true;
    const blob = `${m.name} ${m.company || ""} ${m.asset_class || ""} ${m.geography || ""} ${m.cohort || ""} ${m.bio || ""}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  return (
    <div data-testid="network-directory-view">
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        <h2
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 30,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          Members.
        </h2>
        <span style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.7 }}>{filtered.length}</span>
        <div style={{ flex: 1 }} />
        <input
          data-testid="directory-search"
          placeholder="search by name, company, asset class…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            width: 280,
            background: BG_ALT,
            border: `1px solid ${CREAM}1f`,
            color: CREAM,
            padding: "9px 14px",
            fontFamily: "'Inter', sans-serif",
            fontSize: 13,
            outline: "none",
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ opacity: 0.5, padding: 30, textAlign: "center" }}>no members match.</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {filtered.map((m) => (
            <div
              key={m.id}
              data-testid={`directory-card-${m.id}`}
              style={{
                background: BG_ALT,
                border: `1px solid ${CREAM}0e`,
                padding: "20px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <MemberAvatar member={m} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em" }}>{m.name}</div>
                  <div style={{ ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.85, marginTop: 3 }}>
                    {m.cohort || "MEMBER"}
                  </div>
                </div>
              </div>
              {m.role && <div style={{ fontSize: 12.5, opacity: 0.75 }}>{m.role}{m.company ? ` · ${m.company}` : ""}</div>}
              {m.asset_class && (
                <div style={{ fontSize: 11.5, opacity: 0.55 }}>
                  {m.asset_class}
                  {m.geography ? ` · ${m.geography}` : ""}
                </div>
              )}
              {m.looking_for && (
                <div
                  style={{
                    background: `${GOLD}11`,
                    border: `1px solid ${GOLD}33`,
                    padding: "8px 12px",
                    fontSize: 11.5,
                    color: CREAM,
                    opacity: 0.9,
                  }}
                >
                  <span style={{ ...monoStyle, fontSize: 8.5, color: GOLD, marginRight: 6 }}>LOOKING FOR</span>
                  {m.looking_for}
                </div>
              )}
              {m.bio && <div style={{ fontSize: 12.5, opacity: 0.65, lineHeight: 1.55 }}>{m.bio}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
// Top-level Network entry
// =============================================================
export default function Network() {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY));

  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
  };

  if (!session) return <LoginWall />;
  return <NetworkApp session={session} onLogout={logout} />;
}
